import React, { useEffect, useRef } from "react";
import InlineSVG from "react-inlinesvg";
import { AnimatePresence, motion } from "framer-motion";

import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";

interface FeedbackCreditWarningProps {
  open: boolean;
  estimatedCredits: number;
  onCancel: () => void;
  onContinue: () => void;
}

function formatCredits(n: number): string {
  const rounded = Math.ceil(n / 1000) * 1000;
  return rounded.toLocaleString();
}

const ENTER = { duration: 0.18, ease: [0.16, 1, 0.3, 1] as const };

export const FeedbackCreditWarning: React.FC<FeedbackCreditWarningProps> = ({
  open,
  estimatedCredits,
  onCancel,
  onContinue,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const min = Math.round(estimatedCredits * 0.9);
  const max = Math.round(estimatedCredits * 1.32);
  const formattedRange =
    estimatedCredits > 0 ? `${formatCredits(min)} - ${formatCredits(max)}` : null;

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current && !panelRef.current.contains(target)) {
        // Don't close on clicks inside the Feedback button itself — that's
        // what opened us, and clicking it again should toggle, not double-close.
        if ((target as Element).closest?.(".open-wormhole")) return;
        onCancel();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onCancel]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby="feedback-credit-warning-title"
          initial={{ opacity: 0, y: -4, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -2, scale: 0.99 }}
          transition={ENTER}
          className="absolute top-[calc(100%+4px)] left-0 z-[30000] w-[320px] bg-app-menu-color rounded-xl border border-solid border-app-menu-color-hover shadow-card p-4 antialiased text-left"
        >
          <Text
            as="h3"
            id="feedback-credit-warning-title"
            size="base"
            weight="semibold"
            color="grey-0"
            className="mb-1 [text-wrap:balance]"
          >
            This might take a minute...
          </Text>
          <Text
            size="sm"
            color="grey-1"
            className="mb-2.5 [text-wrap:pretty]"
          >
            Feedback can take longer and use more credits on documents of this size.
          </Text>
          {formattedRange && (
            <div className="mb-3 flex items-center gap-1.5">
              <InlineSVG
                src="/assets/lightning.svg"
                className="h-4 w-4 flex-shrink-0 [&_path]:fill-app-grey-1"
                aria-hidden
              />
              <Text
                size="sm"
                color="grey-1"
                className="tabular-nums"
              >
                Estimated credits: {formattedRange}
              </Text>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <SudoButton
              variant="secondary"
              size="small"
              onClick={onCancel}
            >
              Cancel
            </SudoButton>
            <SudoButton
              variant="primary"
              size="small"
              onClick={onContinue}
            >
              Continue
            </SudoButton>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackCreditWarning;
